/*
 * My Shout draw verifier. One file, so anyone can read the whole thing.
 *
 * Copyright 2026 NetPotential Ltd
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * What this file checks, given a draw's published files:
 *   1. the commitment was signed by the My Shout draw key
 *   2. the commitment sits in the public hash chain
 *   3. the committed drand round was still in the future at seal, the beacon
 *      for it carries a valid BLS signature from the League of Entropy
 *      quicknet, and randomness = SHA-256(signature)
 *   4. the result was signed by the same key and names that beacon
 *   5. the published leaves rebuild the committed Merkle root and group hash
 *   6. every winner is what the selection algorithm gives from that randomness
 *   7. no group (person) won twice
 *
 * It runs in a browser, in a Cloudflare Worker and in Node 20+. The only
 * dependencies are WebCrypto (SHA-256, HMAC) and @noble/curves (Ed25519,
 * BLS12-381), because WebCrypto has neither Ed25519 everywhere nor BLS.
 *
 * Byte encodings are spelled out in docs/DRAW.md and repeated next to the
 * code below. Change nothing here without bumping the version string.
 */
import { ed25519 } from "@noble/curves/ed25519";
import { bls12_381 } from "@noble/curves/bls12-381";

export const VERSION = "myshout-v1";

// ---------------------------------------------------------------------------
// Bytes

export function hexToBytes(hex: string): Uint8Array {
  if (typeof hex !== "string" || hex.length % 2 !== 0 || /[^0-9a-f]/i.test(hex)) throw new Error("bad hex");
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function bytesToHex(b: Uint8Array): string {
  let s = "";
  for (let i = 0; i < b.length; i++) s += b[i].toString(16).padStart(2, "0");
  return s;
}

export const utf8 = (s: string): Uint8Array => new TextEncoder().encode(s);

export function concat(...parts: Uint8Array[]): Uint8Array {
  let n = 0;
  for (const p of parts) n += p.length;
  const out = new Uint8Array(n);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

/** Big-endian unsigned 32-bit. */
export function u32be(n: number): Uint8Array {
  if (!Number.isInteger(n) || n < 0 || n > 0xffffffff) throw new Error("u32 out of range");
  const b = new Uint8Array(4);
  new DataView(b.buffer).setUint32(0, n);
  return b;
}

/** Big-endian unsigned 64-bit. */
export function u64be(n: bigint): Uint8Array {
  const b = new Uint8Array(8);
  new DataView(b.buffer).setBigUint64(0, n);
  return b;
}

/**
 * A length-prefixed field: 4-byte big-endian byte length, then the bytes.
 * Every hashed message in this protocol is a sequence of these, so no two
 * field splits can produce the same byte string.
 */
export function lp(field: Uint8Array): Uint8Array {
  return concat(u32be(field.length), field);
}

export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", data as BufferSource));
}

export async function hmacSha256(key: Uint8Array, msg: Uint8Array): Promise<Uint8Array> {
  const k = await crypto.subtle.importKey("raw", key as BufferSource, { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  return new Uint8Array(await crypto.subtle.sign("HMAC", k, msg as BufferSource));
}

// ---------------------------------------------------------------------------
// Leaves and groups

/**
 * leaf = SHA-256( lp("myshout-v1") | lp(draw_id) | lp(player_id) | lp(u32be(entry_no)) | lp(player_salt) )
 * Strings are UTF-8. player_salt is 32 raw bytes (given here as hex). The
 * salt is what makes a leaf meaningless to anyone but its owner.
 */
export async function leafHash(drawId: string, playerId: string, entryNo: number, playerSaltHex: string): Promise<string> {
  const msg = concat(lp(utf8(VERSION)), lp(utf8(drawId)), lp(utf8(playerId)), lp(u32be(entryNo)), lp(hexToBytes(playerSaltHex)));
  return bytesToHex(await sha256(msg));
}

/**
 * group_id = SHA-256( lp(player_id) | lp(draw_salt) )
 * draw_salt is 32 random bytes chosen at seal and never published, so groups
 * cannot be linked to players or across draws. All of one player's leaves in
 * a draw share a group, which is how "one win per person" is checked.
 */
export async function groupId(playerId: string, drawSaltHex: string): Promise<string> {
  return bytesToHex(await sha256(concat(lp(utf8(playerId)), lp(hexToBytes(drawSaltHex)))));
}

/** group_ids_hash = SHA-256 of the group ids (raw bytes) in leaf order. */
export async function groupIdsHash(groups: string[]): Promise<string> {
  return bytesToHex(await sha256(concat(...groups.map(hexToBytes))));
}

// ---------------------------------------------------------------------------
// Merkle tree: sorted-pair SHA-256, odd tail duplicated (OpenZeppelin shape,
// same as Tournamental). A proof is just sibling hashes, no direction bits.

export const EMPTY_ROOT = "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855"; // sha256("")

export async function pairHash(a: string, b: string): Promise<string> {
  const [lo, hi] = a < b ? [a, b] : [b, a];
  return bytesToHex(await sha256(concat(hexToBytes(lo), hexToBytes(hi))));
}

export function isSorted(leaves: string[]): boolean {
  for (let i = 1; i < leaves.length; i++) if (leaves[i - 1] > leaves[i]) return false;
  return true;
}

/** All levels, leaves first. levels[levels.length - 1][0] is the root. */
export async function merkleLevels(leaves: string[]): Promise<string[][]> {
  if (leaves.length === 0) return [[EMPTY_ROOT]];
  const levels: string[][] = [leaves.slice()];
  while (levels[levels.length - 1].length > 1) {
    const cur = levels[levels.length - 1].slice();
    if (cur.length % 2 === 1) cur.push(cur[cur.length - 1]);
    const next: string[] = [];
    for (let i = 0; i < cur.length; i += 2) next.push(await pairHash(cur[i], cur[i + 1]));
    levels.push(next);
  }
  return levels;
}

export async function merkleRoot(leaves: string[]): Promise<string> {
  const levels = await merkleLevels(leaves);
  return levels[levels.length - 1][0];
}

/** Sibling path for leaf at index i, from the levels of merkleLevels(). */
export function merkleProof(levels: string[][], index: number): string[] {
  const path: string[] = [];
  let i = index;
  for (let l = 0; l < levels.length - 1; l++) {
    const level = levels[l];
    const sib = i ^ 1;
    path.push(sib < level.length ? level[sib] : level[i]); // odd tail pairs with itself
    i = i >> 1;
  }
  return path;
}

export async function verifyInclusion(leaf: string, path: string[], root: string): Promise<boolean> {
  let h = leaf;
  for (const s of path) h = await pairHash(h, s);
  return h === root;
}

// ---------------------------------------------------------------------------
// Canonical JSON, hashing and signatures

/** Sorted keys, no whitespace, no undefined. Arrays keep their order. */
export function canonicalJson(v: unknown): string {
  if (v === null || typeof v !== "object") {
    if (v === undefined) throw new Error("undefined is not canonical");
    if (typeof v === "number" && !Number.isFinite(v)) throw new Error("non-finite number");
    return JSON.stringify(v);
  }
  if (Array.isArray(v)) return "[" + v.map(canonicalJson).join(",") + "]";
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o).filter((k) => o[k] !== undefined).sort();
  return "{" + keys.map((k) => JSON.stringify(k) + ":" + canonicalJson(o[k])).join(",") + "}";
}

/** Everything but the signature field, canonicalised. This is what gets signed and hashed. */
export function signedBody(doc: object): Uint8Array {
  const { signature: _sig, ...body } = doc as { signature?: string };
  return utf8(canonicalJson(body));
}

export async function bodyHash(doc: object): Promise<string> {
  return bytesToHex(await sha256(signedBody(doc)));
}

export function verifySignature(doc: object, publicKeyHex: string): boolean {
  const sig = (doc as { signature?: string }).signature;
  if (!sig) return false;
  try {
    return ed25519.verify(hexToBytes(sig), signedBody(doc), hexToBytes(publicKeyHex), { zip215: false });
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Public hash chain: chain/index.json in the log

export const CHAIN_GENESIS = "0".repeat(64);

export interface ChainEntry {
  seq: number;
  draw_id: string;
  commitment_hash: string;
  prev_hash: string;
  hash: string;
}
export interface ChainIndex {
  head: string;
  entries: ChainEntry[];
}

/** hash = SHA-256(canonical({ seq, draw_id, commitment_hash, prev_hash })) */
export async function chainEntryHash(e: Omit<ChainEntry, "hash">): Promise<string> {
  return bytesToHex(await sha256(utf8(canonicalJson({ seq: e.seq, draw_id: e.draw_id, commitment_hash: e.commitment_hash, prev_hash: e.prev_hash }))));
}

// ---------------------------------------------------------------------------
// drand quicknet (League of Entropy)

export const QUICKNET = {
  chainHash: "52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971",
  publicKey:
    "83cf0f2896adee7eb8b5f01fcad3912212c437e0073e911fb90022d3e760183c8c4b450b6a0a6c3ac6a5776a2d1064510d1fec758c921cc22b0e17e63aaf4bcb5ed66304de9cf809bd274ca73bab4af5a6e9c76a4bc09e76eae8991ef5ece45a",
  period: 3,
  genesis: 1692803367,
  scheme: "bls-unchained-g1-rfc9380",
  dst: "BLS_SIG_BLS12381G1_XMD:SHA-256_SSWU_RO_NUL_",
} as const;

export interface Beacon {
  round: number;
  signature: string;
  randomness?: string;
}

/** R = floor((T - genesis) / period) + 1, T in unix seconds. */
export function drandRoundAt(unixSeconds: number): number {
  if (unixSeconds < QUICKNET.genesis) return 1;
  return Math.floor((unixSeconds - QUICKNET.genesis) / QUICKNET.period) + 1;
}

/** Unix seconds at which round R is published. */
export function drandRoundTime(round: number): number {
  return QUICKNET.genesis + (round - 1) * QUICKNET.period;
}

/**
 * Unchained scheme: the signed message is SHA-256(round as 8-byte big-endian),
 * signature on G1 (48 bytes), public key on G2 (96 bytes).
 * Returns the randomness (SHA-256 of the signature) when valid, else null.
 */
export async function verifyBeacon(b: Beacon): Promise<string | null> {
  try {
    if (!Number.isInteger(b.round) || b.round < 1) return null;
    const sig = hexToBytes(b.signature);
    if (sig.length !== 48) return null;
    const msg = await sha256(u64be(BigInt(b.round)));
    const point = bls12_381.shortSignatures.hash(msg, QUICKNET.dst);
    if (!bls12_381.shortSignatures.verify(sig, point, hexToBytes(QUICKNET.publicKey))) return null;
    const randomness = bytesToHex(await sha256(sig));
    if (b.randomness && b.randomness !== randomness) return null;
    return randomness;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Selection

export interface Selected {
  prize_id: string;
  leaf_index: number;
  group_id: string;
  attempt: number;
}

const TWO64 = 1n << 64n;
const MAX_ATTEMPTS = 1_000_000; // never reached in practice; keeps a bad input from spinning

/**
 * x = first 8 bytes of h, big-endian unsigned. Rejected (null) when
 * x >= 2^64 - (2^64 mod n), so every index 0..n-1 is exactly equally likely.
 */
export function indexFromHash(h: Uint8Array, n: bigint): number | null {
  if (h.length < 8 || n < 1n) throw new Error("bad index input");
  const x = new DataView(h.buffer, h.byteOffset, 8).getBigUint64(0);
  const limit = TWO64 - (TWO64 % n);
  return x >= limit ? null : Number(x % n);
}

/**
 * For prize k in order, attempt c from 0:
 *   h = HMAC-SHA256(key = randomness, msg = lp(draw_id) | lp(prize_id) | lp(u32be(c)))
 *   x = first 8 bytes of h, big-endian unsigned
 *   reject (c += 1) if x >= 2^64 - (2^64 mod n), else index = x mod n
 *   if groups[index] already won this draw, c += 1 and try again
 * A prize is unawarded only when every group has already won.
 */
export async function selectWinners(drawId: string, prizeIds: string[], randomnessHex: string, groups: string[]): Promise<{ winners: Selected[]; unawarded: string[] }> {
  const n = BigInt(groups.length);
  const winners: Selected[] = [];
  const unawarded: string[] = [];
  if (groups.length === 0) return { winners, unawarded: prizeIds.slice() };
  const key = hexToBytes(randomnessHex);
  const won = new Set<string>();
  const distinct = new Set(groups).size;
  for (const prizeId of prizeIds) {
    if (won.size >= distinct) {
      unawarded.push(prizeId);
      continue;
    }
    let found = false;
    for (let c = 0; c < MAX_ATTEMPTS; c++) {
      const h = await hmacSha256(key, concat(lp(utf8(drawId)), lp(utf8(prizeId)), lp(u32be(c))));
      const index = indexFromHash(h, n);
      if (index === null) continue;
      const g = groups[index];
      if (won.has(g)) continue;
      won.add(g);
      winners.push({ prize_id: prizeId, leaf_index: index, group_id: g, attempt: c });
      found = true;
      break;
    }
    if (!found) unawarded.push(prizeId);
  }
  return { winners, unawarded };
}

// ---------------------------------------------------------------------------
// The published documents

export interface CommitmentPrize {
  id: string;
  title: string;
  sponsor: string;
}
export interface Commitment {
  version: string;
  draw_id: string;
  region: string;
  game_day: string;
  prizes: CommitmentPrize[];
  n_entries: number;
  merkle_root: string;
  group_ids_hash: string;
  drand_chain: string;
  drand_round: number;
  sealed_at: string;
  public_key: string;
  signature?: string;
}
export interface Winner {
  prize_id: string;
  leaf_index: number;
  leaf: string;
  group_id: string;
  attempt: number;
}
export interface DrawResult {
  version: string;
  draw_id: string;
  commitment_hash: string;
  drand: {
    round: number;
    committed_round: number;
    fallbacks: number; // round = committed_round + 100 * fallbacks
    fallback_reason: string | null;
    signature: string;
    randomness: string;
  };
  winners: Winner[];
  unawarded: string[];
  drawn_at: string;
  public_key: string;
  signature?: string;
}
export interface LeavesFile {
  draw_id: string;
  n: number;
  leaves: string[];
  groups: string[];
}

export interface Check {
  name: string;
  ok: boolean;
  detail: string;
}

export interface VerifyInput {
  commitment: Commitment;
  result: DrawResult;
  beacon: Beacon;
  publicKey: string;
  leaves?: LeavesFile;
  chain?: ChainIndex;
}

/** The whole thing. Every check runs even after one fails, so the list is complete. */
export async function verifyDraw(input: VerifyInput): Promise<{ ok: boolean; checks: Check[] }> {
  const { commitment: c, result: r, beacon, publicKey } = input;
  const checks: Check[] = [];
  const add = (name: string, ok: boolean, detail: string) => checks.push({ name, ok, detail });

  // 1. commitment signature
  const cSig = c.public_key === publicKey && verifySignature(c, publicKey);
  add("Commitment signed by the My Shout draw key", cSig, cSig ? `Ed25519 key ${publicKey.slice(0, 16)}…` : "signature or key mismatch");
  const cHash = await bodyHash(c);
  add("Commitment version", c.version === VERSION, c.version);

  // 2. chain link
  if (input.chain) {
    const entries = input.chain.entries;
    const i = entries.findIndex((e) => e.draw_id === c.draw_id);
    if (i < 0) add("Commitment is in the public hash chain", false, "no chain entry for this draw");
    else {
      const e = entries[i];
      const prevOk = i === 0 ? e.prev_hash === CHAIN_GENESIS : e.prev_hash === entries[i - 1].hash;
      const hashOk = (await chainEntryHash(e)) === e.hash;
      const ok = e.commitment_hash === cHash && prevOk && hashOk;
      add("Commitment is in the public hash chain", ok, ok ? `entry #${e.seq}, hash ${e.hash.slice(0, 16)}…` : `commitment ${e.commitment_hash === cHash}, link ${prevOk}, hash ${hashOk}`);
    }
  } else add("Commitment is in the public hash chain", false, "chain index not supplied");

  // 3. drand. The committed round must still have been in the future when
  // the list was sealed, otherwise the operator knew the randomness before
  // committing, and fallbacks may only move forward in whole steps of 100.
  const sealedAtMs = Date.parse(c.sealed_at);
  const futureOk = Number.isInteger(c.drand_round) && c.drand_round >= 1 && Number.isFinite(sealedAtMs) && drandRoundTime(c.drand_round) * 1000 > sealedAtMs;
  add("Committed round was still in the future at seal", futureOk, futureOk ? `round ${c.drand_round} at ${new Date(drandRoundTime(c.drand_round) * 1000).toISOString()}, sealed ${c.sealed_at}` : `round ${c.drand_round}, sealed ${c.sealed_at}`);
  const fallbacksOk = Number.isInteger(r.drand.fallbacks) && r.drand.fallbacks >= 0;
  const expectedRound = c.drand_round + 100 * (fallbacksOk ? r.drand.fallbacks : 0);
  const roundOk = fallbacksOk && c.drand_chain === QUICKNET.chainHash && r.drand.committed_round === c.drand_round && r.drand.round === expectedRound && beacon.round === r.drand.round;
  add(
    "Beacon round matches the committed round",
    roundOk,
    roundOk
      ? r.drand.fallbacks
        ? `round ${beacon.round} = ${c.drand_round} + 100 × ${r.drand.fallbacks} (published reason: ${r.drand.fallback_reason || "none"})`
        : `round ${beacon.round}`
      : `committed ${c.drand_round}, result ${r.drand.round}, beacon ${beacon.round}`,
  );
  if (r.drand.fallbacks) add("Fallback round has a published reason", !!r.drand.fallback_reason, r.drand.fallback_reason || "missing");
  const randomness = await verifyBeacon(beacon);
  add("drand BLS signature verifies against the quicknet public key", randomness !== null, randomness ? "BLS12-381 G1, bls-unchained-g1-rfc9380" : "invalid signature");
  const randOk = randomness !== null && r.drand.signature === beacon.signature && r.drand.randomness === randomness;
  add("Randomness = SHA-256(signature) and matches the result", randOk, randOk ? randomness : "mismatch");

  // 4. result signature
  const rSig = r.public_key === publicKey && verifySignature(r, publicKey) && r.draw_id === c.draw_id && r.commitment_hash === cHash;
  add("Result signed by the same key and bound to this commitment", rSig, rSig ? `commitment hash ${cHash.slice(0, 16)}…` : "signature, draw id or commitment hash mismatch");

  // 5. leaves
  let groups: string[] | null = null;
  if (input.leaves) {
    const L = input.leaves;
    const countOk = L.leaves.length === c.n_entries && L.groups.length === c.n_entries && L.n === c.n_entries && L.draw_id === c.draw_id;
    const sortedOk = isSorted(L.leaves);
    const root = await merkleRoot(L.leaves);
    const gh = await groupIdsHash(L.groups);
    const ok = countOk && sortedOk && root === c.merkle_root && gh === c.group_ids_hash;
    add("Published leaves rebuild the committed Merkle root", ok, ok ? `${L.leaves.length} entries, root ${root.slice(0, 16)}…` : `count ${countOk}, sorted ${sortedOk}, root ${root === c.merkle_root}, groups ${gh === c.group_ids_hash}`);
    if (ok) groups = L.groups;
  } else add("Published leaves rebuild the committed Merkle root", false, "leaves not supplied");

  // 6. selection
  if (groups && randomness) {
    const sel = await selectWinners(c.draw_id, c.prizes.map((p) => p.id), randomness, groups);
    const L = input.leaves!;
    const same =
      sel.winners.length === r.winners.length &&
      sel.winners.every((w, i) => {
        const x = r.winners[i];
        return x && x.prize_id === w.prize_id && x.leaf_index === w.leaf_index && x.group_id === w.group_id && x.attempt === w.attempt && x.leaf === L.leaves[w.leaf_index];
      }) &&
      sel.unawarded.length === r.unawarded.length &&
      sel.unawarded.every((p, i) => r.unawarded[i] === p);
    add("Every winner is what the selection algorithm gives", same, same ? `${sel.winners.length} winner(s), ${sel.unawarded.length} unawarded` : "recomputed winners differ from the published result");
  } else add("Every winner is what the selection algorithm gives", false, "needs valid leaves and a valid beacon");

  // 7. one win per group
  const gs = r.winners.map((w) => w.group_id);
  const oneEach = new Set(gs).size === gs.length;
  add("One win per person (group)", oneEach, oneEach ? `${gs.length} distinct group(s)` : "a group appears twice");

  return { ok: checks.every((x) => x.ok), checks };
}
